import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ShoppingBag, Package } from "lucide-react";
import { toast } from "sonner";

const money = (cents, currency = "usd") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "usd").toUpperCase() }).format((cents || 0) / 100);

export default function ProductDetail() {
  const { business_id, pid } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [photoIdx, setPhotoIdx] = useState(0);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/shop/products/${pid}`);
        if (!alive) return;
        const prod = r.data.product;
        setP(prod);
        if (prod.sizes?.length) setSize(prod.sizes[0]);
        if (prod.colors?.length) setColor(prod.colors[0]);
      } catch (e) {
        if (alive) toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [pid]);

  const buy = async () => {
    if (!user) { toast("Sign in to buy"); navigate("/login"); return; }
    setBuying(true);
    try {
      const r = await api.post(`/shop/products/${pid}/buy`, {
        qty,
        size,
        color,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setBuying(false);
    }
  };

  if (loading) return <div className="max-w-4xl mx-auto p-8 text-[#8B7B73]">Loading…</div>;
  if (!p) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <Package className="mx-auto text-[#B31E1E] mb-3" size={30} />
        <p className="font-black text-2xl">Product not found</p>
        <Link to={`/shop/${business_id}`} className="mt-3 inline-block text-[#B31E1E] hover:underline">← Back to shop</Link>
      </div>
    );
  }

  const cap = Math.max(1, Math.min(20, p.inventory_qty ?? 20));
  const total = money((p.price_cents || 0) * qty, p.currency);

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-8 pb-20" data-testid="product-detail-page">
      <Link to={`/shop/${business_id}`} className="inline-flex items-center gap-1 text-sm text-[#B31E1E] hover:underline mb-5">
        <ArrowLeft size={14} /> Back to shop
      </Link>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
        {/* Photos */}
        <div className="md:col-span-3 space-y-3">
          <Card className="rounded-md overflow-hidden aspect-square bg-gray-100" data-testid="product-hero-photo">
            {p.photos?.[photoIdx] ? (
              <img src={p.photos[photoIdx]} alt={p.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400"><ShoppingBag size={60} /></div>
            )}
          </Card>
          {p.photos?.length > 1 && (
            <div className="flex gap-2 overflow-x-auto">
              {p.photos.map((ph, i) => (
                <button
                  key={i}
                  onClick={() => setPhotoIdx(i)}
                  className={`shrink-0 w-16 h-16 rounded-sm overflow-hidden border-2 transition-colors ${i === photoIdx ? "border-[#B31E1E]" : "border-transparent"}`}
                  data-testid={`product-thumb-${i}`}
                >
                  <img src={ph} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info + buy */}
        <div className="md:col-span-2 space-y-4">
          {p.category && (
            <Badge className="rounded-sm bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-100 text-[10px] font-mono uppercase tracking-wider">{p.category}</Badge>
          )}
          <h1 className="text-3xl font-black tracking-tight text-[#1B1614]">{p.title}</h1>
          <div className="text-3xl font-black text-[#B31E1E] font-mono">{money(p.price_cents, p.currency)}</div>
          {p.description && (
            <p className="text-[15px] leading-relaxed text-[#5C4E48] whitespace-pre-wrap" data-testid="product-description">{p.description}</p>
          )}

          {p.sizes?.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider font-mono text-[#5C4E48] mb-1.5">Size</p>
              <div className="flex flex-wrap gap-2" data-testid="product-sizes">
                {p.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    className={`px-3 py-1.5 rounded-sm text-sm border transition-colors ${size === s ? "bg-[#B31E1E] text-white border-[#B31E1E]" : "bg-white text-[#1B1614] border-[#EAE3DE] hover:border-[#B31E1E]"}`}
                    data-testid={`product-size-${s}`}
                  >{s}</button>
                ))}
              </div>
            </div>
          )}
          {p.colors?.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider font-mono text-[#5C4E48] mb-1.5">Color</p>
              <div className="flex flex-wrap gap-2" data-testid="product-colors">
                {p.colors.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    className={`px-3 py-1.5 rounded-sm text-sm border transition-colors ${color === c ? "bg-[#B31E1E] text-white border-[#B31E1E]" : "bg-white text-[#1B1614] border-[#EAE3DE] hover:border-[#B31E1E]"}`}
                    data-testid={`product-color-${c}`}
                  >{c}</button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <label className="text-xs text-[#5C4E48]">Qty</label>
            <Input
              type="number"
              min={1}
              max={cap}
              value={qty}
              onChange={(e) => setQty(Math.max(1, Math.min(cap, parseInt(e.target.value || "1", 10))))}
              className="rounded-sm w-20 h-9 font-mono"
              data-testid="product-qty-input"
            />
            {p.inventory_qty !== null && p.inventory_qty !== undefined && (
              <span className="text-[10px] font-mono text-[#8B7B73]">
                {p.inventory_qty > 0 ? `${p.inventory_qty} in stock` : "Sold out"}
              </span>
            )}
          </div>

          <div className="pt-2 border-t border-[#EAE3DE]">
            <div className="flex justify-between mb-2 text-sm">
              <span className="text-[#5C4E48]">Total</span>
              <span className="font-black">{total}</span>
            </div>
            <Button
              onClick={buy}
              disabled={buying || !p.in_stock}
              className="w-full rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717] text-white"
              data-testid="product-buy-button"
            >
              <ShoppingBag size={14} className="mr-1.5" />
              {!p.in_stock ? "Sold out" : buying ? "Redirecting…" : "Buy now"}
            </Button>
            <p className="text-[10px] text-[#8B7B73] mt-2 text-center leading-relaxed">
              Secure checkout by Stripe. Servrevu&rsquo;s 2% + Stripe&rsquo;s 2.9% + $0.30 processing (4.9% total) are deducted from the seller&rsquo;s payout.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
